import React from "react";
import { useLandingLang } from "../../i18n/LandingLangContext";

// --- Iconos SVG Inline (Lucide) ---

function Settings2Icon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M20 7h-9" />
      <path d="M14 17H5" />
      <circle cx="17" cy="17" r="3" />
      <circle cx="7" cy="7" r="3" />
    </svg>
  );
}

function LayersIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
      <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
      <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
    </svg>
  );
}

function PaletteIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 3a9 9 0 0 0 0 18h2a2 2 0 0 0 0-4h-1a2 2 0 0 1 0-4h2a4 4 0 0 0 0-8h-5Z" />
      <circle cx="8.5" cy="10" r="1" fill="currentColor" />
      <circle cx="7.5" cy="14" r="1" fill="currentColor" />
      <circle cx="11" cy="8.5" r="1" fill="currentColor" />
    </svg>
  );
}

function ClipboardCheckIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

function LineChartIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M3 3v18h18" />
      <path d="m19 9-5 5-4-4-3 3" />
    </svg>
  );
}

function ImageIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  );
}

export function Validation({
  resolveImage,
  creationPanelKey,
  clinicalAnalyticsKey,
}) {
  const { t } = useLandingLang();
  const topImg =
    typeof resolveImage === "function" ? resolveImage(creationPanelKey) : null;
  const bottomImg =
    typeof resolveImage === "function"
      ? resolveImage(clinicalAnalyticsKey)
      : null;

  return (
    // Sección principal con fondo oscuro y bordes redondeados grandes
    <section
      id="herramientas"
      className="py-24 px-6 bg-slate-900 text-white rounded-[3.5rem] mx-4 overflow-hidden relative border border-white/5"
    >
      {/* Blob de luz azul de fondo */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/10 blur-[100px] -z-0"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-[1fr_450px] gap-16 items-start">
          {/* --- COLUMNA IZQUIERDA: Textos y Features --- */}
          <div className="space-y-8 text-center lg:text-left">
            {/* Kicker */}
            <div className="inline-flex items-center gap-2 text-sky-400 !font-black uppercase tracking-widest text-xs">
              <Settings2Icon className="w-4 h-4" /> {t("validationTag")}
            </div>

            {/* Título Principal */}
            <h2 className="text-4xl lg:text-5xl !font-black leading-tight text-white">
              {t("validationTitle")}
            </h2>

            {/* Descripción */}
            <p className="text-slate-400 text-lg leading-relaxed font-medium">
              {t("validationDesc")}
            </p>

            {/* Grid de Features (2 columnas en móviles/desktop) */}
            <div className="grid sm:grid-cols-2 gap-6 text-left">
              {/* Feature 1 */}
              <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="text-sky-400 shrink-0">
                  <LayersIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="!font-bold mb-1 text-white uppercase tracking-tight text-sm">
                    {t("val1Title")}
                  </h4>
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    {t("val1Desc")}
                  </p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="text-sky-400 shrink-0">
                  <PaletteIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="!font-bold mb-1 text-white uppercase tracking-tight text-sm">
                    {t("val2Title")}
                  </h4>
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    {t("val2Desc")}
                  </p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="text-emerald-400 shrink-0">
                  <ClipboardCheckIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="!font-bold mb-1 text-white uppercase tracking-tight text-sm">
                    {t("val3Title")}
                  </h4>
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    {t("val3Desc")}
                  </p>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="flex gap-4 p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group">
                <div className="text-emerald-400 shrink-0">
                  <LineChartIcon className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="!font-bold mb-1 text-white uppercase tracking-tight text-sm">
                    {t("val4Title")}
                  </h4>
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    {t("val4Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* --- COLUMNA DERECHA: Imágenes Flotantes --- */}
          <div className="space-y-6">
            {/* Imagen 1 (Creación) */}
            <div className="aspect-[4/3] bg-white/5 rounded-[2.5rem] border border-white/10 p-6 flex items-center justify-center group overflow-hidden relative">
              <div className="w-full h-full bg-slate-800 rounded-2xl border border-white/5 flex items-center justify-center relative overflow-hidden shadow-2xl">
                {topImg ? (
                  <img
                    src={topImg}
                    alt="Gestión"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-slate-700" />
                )}
              </div>
              <div className="absolute top-4 right-4 bg-sky-500 px-3 py-1 rounded-full text-[9px] !font-bold uppercase tracking-widest text-white shadow-lg whitespace-nowrap">
                {t("valBadge1")}
              </div>
            </div>

            {/* Imagen 2 (Analítica) */}
            <div className="aspect-[4/3] bg-white/5 rounded-[2.5rem] border border-white/10 p-6 flex items-center justify-center group overflow-hidden relative">
              <div className="w-full h-full bg-slate-800 rounded-2xl border border-white/5 flex items-center justify-center relative overflow-hidden shadow-2xl">
                {bottomImg ? (
                  <img
                    src={bottomImg}
                    alt="Analítica"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                ) : (
                  <LineChartIcon className="w-12 h-12 text-slate-700" />
                )}
              </div>
              <div className="absolute top-4 right-4 bg-emerald-500 px-3 py-1 rounded-full text-[9px] !font-bold uppercase tracking-widest text-white shadow-lg whitespace-nowrap">
                {t("valBadge2")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
